/**
 * ProductList.js
 * 제품 목록 표시 컴포넌트 (React Query 적용 + 페이징)
 * 
 * 주요 기능:
 * - 제품 목록을 그리드 형태로 표시
 * - 카테고리별 필터링
 * - 할인율 계산 및 표시
 * - 제품 상세보기 모달
 * - 다양한 리스트 타입 지원 (전체/잉여재고/행사품목)
 * - React Query를 통한 데이터 캐싱 (메뉴 이동 시 재로딩 방지)
 * - 페이지네이션 기능 (30개 고정)
 * - Scroll to Top 플로팅 버튼
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ProductCard from '../dashboard/ProductCard';
import { addToCart } from '../../utils/cartUtils'; // Import cart util
import Modal from '../common/Modal'; // Import Modal

// 기존 Component imports 중복 제거 및 정리
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ChevronUp, Package, Search, RotateCcw, ChevronDown, ChevronsLeft, ChevronsRight } from 'lucide-react';
import './ProductList.css';
import { preloadImages } from '../common/OptimizedImage';
import '../common/OptimizedImage.css';
import QuoteModal from '../modals/QuoteModal';
import { productAPI, commonAPI } from '../../services/api';

// ... (QUERY_KEYS and ITEMS_PER_PAGE remain same)

// 쿼리 키 상수
const QUERY_KEYS = {
  SURPLUS_PRODUCTS: 'surplusProducts',
  EVENT_PRODUCTS: 'eventProducts',
  ALL_PRODUCTS: 'allProducts',
};

// 페이지당 아이템 수 (고정)
const ITEMS_PER_PAGE = 30;

/**
 * ProductList 컴포넌트
 */
const ProductList = ({ selectedCategory, listType = 'all', onClose, onProductCountUpdate }) => {
  const navigate = useNavigate();
  // Auth Context 사용
  const { globalState } = useAuth();
  const isLoggedIn = !!globalState.G_USER_ID;

  // 모달 상태 관리
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showCartSuccessModal, setShowCartSuccessModal] = useState(false); // Success Modal State

  // ... (Paging state, scroll top, search logic remain same - keep them)
  const [currentPage, setCurrentPage] = useState(1);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [itemName, setItemName] = useState('');
  const [selectedCategoryL, setSelectedCategoryL] = useState('');
  const [selectedCategoryM, setSelectedCategoryM] = useState('');
  const [selectedCategoryS, setSelectedCategoryS] = useState('');
  const [categoryLData, setCategoryLData] = useState([]);
  const [categoryMData, setCategoryMData] = useState([]);
  const [categorySData, setCategorySData] = useState([]);
  const [isSearchVisible, setIsSearchVisible] = useState(window.innerWidth > 768);
  const searchToggleRef = useRef(null);

  // ... (useEffect for categories, resize, etc. - keep them)

  // 카테고리 데이터 로드 (Event용)
  useEffect(() => {
    if (listType === 'event') {
      const loadCategories = async () => {
        try {
          const [lData, mData, sData] = await Promise.all([
            commonAPI.getCategoryL(),
            commonAPI.getCategoryM(),
            commonAPI.getCategoryS()
          ]);
          setCategoryLData(lData || []);
          setCategoryMData(mData || []);
          setCategorySData(sData || []);
        } catch (error) {
          console.error('카테고리 로드 실패:', error);
        }
      };
      loadCategories();
    }
  }, [listType]);

  // 화면 크기 변경 감지하여 검색 필터 자동 펼치기
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsSearchVisible(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCategoryLChange = (value) => {
    setSelectedCategoryL(value);
    setSelectedCategoryM('');
    setSelectedCategoryS('');
  };

  const handleCategoryMChange = (value) => {
    setSelectedCategoryM(value);
    setSelectedCategoryS('');
  };

  const getFilteredCategoryM = () => {
    if (!selectedCategoryL) return categoryMData;
    return categoryMData.filter(item => item.catLCd === selectedCategoryL);
  };

  const getFilteredCategoryS = () => {
    if (!selectedCategoryM) return [];
    return categorySData.filter(item => item.catMCd === selectedCategoryM);
  };

  const handleSearch = () => {
    refetch();
    if (window.innerWidth <= 768) setIsSearchVisible(false);
  };

  const handleReset = () => {
    setItemName('');
    setSelectedCategoryL('');
    setSelectedCategoryM('');
    setSelectedCategoryS('');
  };

  const queryKey = useMemo(() => {
    const baseKey = listType === 'surplus'
      ? QUERY_KEYS.SURPLUS_PRODUCTS
      : listType === 'event'
        ? QUERY_KEYS.EVENT_PRODUCTS
        : QUERY_KEYS.ALL_PRODUCTS;

    if (listType === 'event') {
      return [baseKey, itemName, selectedCategoryL, selectedCategoryM, selectedCategoryS];
    }

    if (selectedCategory?.catCd) {
      return [baseKey, selectedCategory.catCd, selectedCategory.level];
    }
    return [baseKey];
  }, [listType, selectedCategory, itemName, selectedCategoryL, selectedCategoryM, selectedCategoryS]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, listType]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchProducts = async () => {
    if (listType === 'surplus') {
      const params = { itemDivCd: '010' };

      if (selectedCategory?.catCd) {
        if (selectedCategory.level === 1) params.itemGroupLCd = selectedCategory.catCd;
        else if (selectedCategory.level === 2) params.itemGroupMCd = selectedCategory.catCd;
        else if (selectedCategory.level === 3) params.itemGroupSCd = selectedCategory.catCd;
      }

      const data = await productAPI.getDashItems(
        params.itemDivCd,
        params.itemGroupLCd,
        params.itemGroupMCd,
        params.itemGroupSCd
      );

      return data.map(item => ({
        ...item,
        id: item.itemCd || item.id,
        name: item.itemNm,
        isSurplus: true,
        isEvent: false
      }));

    } else if (listType === 'event') {
      const params = { itemDivCd: '020' };

      if (selectedCategoryL) params.itemGroupLCd = selectedCategoryL;
      if (selectedCategoryM) params.itemGroupMCd = selectedCategoryM;
      if (selectedCategoryS) params.itemGroupSCd = selectedCategoryS;

      if (selectedCategory?.catCd) {
        if (selectedCategory.level === 1) params.itemGroupLCd = selectedCategory.catCd;
        else if (selectedCategory.level === 2) params.itemGroupMCd = selectedCategory.catCd;
        else if (selectedCategory.level === 3) params.itemGroupSCd = selectedCategory.catCd;
      }

      const data = await productAPI.getDashItems(
        params.itemDivCd,
        params.itemGroupLCd,
        params.itemGroupMCd,
        params.itemGroupSCd
      );

      let filteredData = data;
      if (itemName.trim()) {
        filteredData = data.filter(item => item.itemNm && item.itemNm.includes(itemName.trim()));
      }

      // Add 'SoldOut' status simulation for testing if needed, or real logic
      // Assuming 'paramStr2' holds status like 'SoldOut'
      return filteredData.map(item => ({
        ...item,
        id: item.itemCd || item.id,
        name: item.itemNm,
        isSurplus: false,
        isEvent: true
      }));

    } else {
      const response = await fetch('/data/products.json');
      if (!response.ok) throw new Error('제품 데이터를 불러올 수 없습니다.');
      const data = await response.json();
      return data.products;
    }
  };

  const {
    data: products = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKey,
    queryFn: fetchProducts,
    staleTime: 3 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { currentItems, totalPages, startIndex, endIndex } = useMemo(() => {
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIdx = startIdx + ITEMS_PER_PAGE;
    return {
      currentItems: products.slice(startIdx, endIdx),
      totalPages: Math.ceil(products.length / ITEMS_PER_PAGE),
      startIndex: startIdx,
      endIndex: Math.min(endIdx, products.length)
    };
  }, [products, currentPage]);

  useEffect(() => {
    if (currentPage < totalPages && products.length > 0) {
      const nextStartIdx = currentPage * ITEMS_PER_PAGE;
      const nextEndIdx = nextStartIdx + ITEMS_PER_PAGE;
      const nextItems = products.slice(nextStartIdx, nextEndIdx);
      const nextImageUrls = nextItems
        .map(p => p.FILEPATH || p.thFilePath || p.filePath)
        .filter(Boolean);

      if (nextImageUrls.length > 0) {
        const timer = setTimeout(() => {
          preloadImages(nextImageUrls);
        }, 800);
        return () => clearTimeout(timer);
      }
    }
  }, [currentPage, totalPages, products]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (onProductCountUpdate) {
      onProductCountUpdate(products.length);
    }
  }, [products.length, onProductCountUpdate]);

  const handleProductView = (product) => {
    setSelectedProduct(product);
    setShowQuoteModal(true);
  };

  const handleCloseModal = () => {
    setShowQuoteModal(false);
    setSelectedProduct(null);
  };

  /**
   * 장바구니 담기 핸들러
   */
  const handleAddToCart = (product) => {
    if (!product) return;

    // 로그인 체크
    if (!isLoggedIn) {
      alert('로그인이 필요한 서비스입니다.');
      // 또는 로그인 모달을 띄울 수 있음 (QuoteModal처럼)
      return;
    }

    try {
      const custCd = globalState.G_CUST_CD || '';
      const price = product.disPrice || product.salePrice || 0;

      // cartUtils를 사용하여 장바구니 추가
      const success = addToCart(custCd, {
        itemCd: product.itemCd,
        itemNm: product.itemNm,
        unitNm: product.unitNm,
        optCd: product.optCd || '',
        optValCd: '',     // 목록에서 바로 담을 때는 옵션 선택 없음 (기본값)
        optValNm: '',
        price: price,
        outUnitPrice: price,
        quantity: 1,      // 기본 수량 1
        filePath: product.FILEPATH,
        totalAmount: price,
        source: product.isSurplus ? 'surplus' : (product.isEvent ? 'event' : 'general'),
        shipAvDate: product.shipAvDate,
        compNm: product.compNm
      });

      if (success) {
        setShowCartSuccessModal(true);
      } else {
        alert('장바구니 담기에 실패했습니다.');
      }

    } catch (e) {
      console.error('장바구니 추가 실패:', e);
      alert('장바구니 담기에 실패했습니다.');
    }
  };

  // 로딩 중
  if (isLoading) {
    return (
      <div className="prd_loading">
        <div className="prd_spinner"></div>
        <p>제품 목록을 불러오는 중...</p>
      </div>
    );
  }

  // 에러 발생
  if (isError) {
    return (
      <div className="prd_error">
        <p>제품 목록을 불러오는데 실패했습니다.</p>
        <p>{error?.message || '알 수 없는 오류가 발생했습니다.'}</p>
        <button onClick={() => refetch()}>다시 시도</button>
      </div>
    );
  }

  // 메인 UI 렌더링
  return (
    <div className={`prd_container ${listType === 'event' ? 'event-mode' : ''}`}>
      {/* 장바구니 추가 성공 모달 */}
      <Modal
        isOpen={showCartSuccessModal}
        title="알림"
        message="상품이 장바구니에 담겼습니다. 장바구니로 이동하시겠습니까?"
        confirmText="장바구니 이동"
        cancelText="쇼핑 계속하기"
        onConfirm={() => {
          setShowCartSuccessModal(false);
          if (onClose) onClose();
          navigate('/cart');
        }}
        onCancel={() => setShowCartSuccessModal(false)}
      />

      {/* 검색 섹션 (Event Page 전용) */}
      {listType === 'event' && (
        <div className="prd_search_section" ref={searchToggleRef}>
          <div className="prd_mobile_search_toggle" onClick={() => setIsSearchVisible(!isSearchVisible)}>
            <span>검색 옵션</span>
            {isSearchVisible ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>

          <div className={`prd_search_container ${isSearchVisible ? 'visible' : 'hidden'}`}>
            <div className="prd_search_row">
              <div className="prd_search_field">
                <label>대분류</label>
                <select value={selectedCategoryL} onChange={(e) => handleCategoryLChange(e.target.value)}>
                  <option value="">전체</option>
                  {categoryLData.map((category) => (
                    <option key={category.catLCd} value={category.catLCd}>{category.catLNm}</option>
                  ))}
                </select>
              </div>

              <div className="prd_search_field">
                <label>중분류</label>
                <select value={selectedCategoryM} onChange={(e) => handleCategoryMChange(e.target.value)} disabled={!selectedCategoryL}>
                  <option value="">전체</option>
                  {getFilteredCategoryM().map((category) => (
                    <option key={category.catMCd} value={category.catMCd}>{category.catMNm}</option>
                  ))}
                </select>
              </div>

              <div className="prd_search_field">
                <label>소분류</label>
                <select value={selectedCategoryS} onChange={(e) => setSelectedCategoryS(e.target.value)} disabled={!selectedCategoryM}>
                  <option value="">전체</option>
                  {getFilteredCategoryS().map((category) => (
                    <option key={category.catSCd} value={category.catSCd}>{category.catSNm}</option>
                  ))}
                </select>
              </div>

              <div className="prd_search_field">
                <label>제품명</label>
                <input
                  type="text"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="제품명 입력"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>

              <div className="prd_search_buttons">
                <button className="prd_search_btn" onClick={handleSearch}>
                  <Search size={16} />
                  조회
                </button>
                <button className="prd_reset_btn" onClick={handleReset} title="검색 초기화">
                  <RotateCcw size={16} />
                  초기화
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 제품 없음 */}
      {
        products.length === 0 ? (
          <div className="prd_empty">
            <Package size={48} color="#ccc" />
            <p>
              {selectedCategory
                ? `선택한 카테고리에 해당하는 제품이 없습니다.`
                : `현재 등록된 제품이 없습니다.`
              }
            </p>
          </div>
        ) : (
          <>
            {/* 제품 그리드 - ProductCard 컴포넌트로 교체 */}
            <div className="prd_grid">
              {currentItems.map((product) => (
                <ProductCard
                  key={product.id || product.itemCd}
                  product={product}
                  onProductClick={handleProductView}
                  isLoggedIn={isLoggedIn}
                  showCartButton={true} // 장바구니 버튼 표시
                  onCartClick={handleAddToCart} // 장바구니 담기 핸들러
                />
              ))}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="prd_pagination">
                {/* 맨 처음으로 */}
                <button
                  className="prd_page_btn prd_page_nav"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  title="처음으로"
                >
                  <ChevronsLeft size={16} />
                </button>

                {/* 수정: 이전 버튼 (1페이지 전) */}
                <button
                  className="prd_page_btn prd_page_nav"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  title="이전"
                >
                  <ChevronLeft size={16} />
                </button>

                {/* 페이지 번호들 */}
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    className={`prd_page_btn ${currentPage === page ? 'prd_page_active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}

                {/* 다음 */}
                <button
                  className="prd_page_btn prd_page_nav"
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  title="다음"
                >
                  <ChevronRight size={16} />
                </button>

                {/* 맨 끝으로 */}
                <button
                  className="prd_page_btn prd_page_nav"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  title="끝으로"
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            )}
          </>
        )
      }

      {/* Scroll to Top 플로팅 버튼 */}
      <button
        className={`prd_scroll_to_top ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="맨 위로"
      >
        <ChevronUp size={24} />
      </button>

      {/* 견적 요청 모달 */}
      <QuoteModal
        product={selectedProduct}
        isOpen={showQuoteModal}
        onClose={handleCloseModal}
      />
    </div >
  );
};

export default ProductList;